/**
 * You › Machines › resources (docs/machine-resources.md): the machine-wide broker's capacity per
 * resource, who holds it and who waits (every row opens to its full details), the screen gate
 * and the disk floor with safe cleanup suggestions. From the machine's encrypted status report.
 */
import { Link } from '@tanstack/react-router'
import type { MachineResourceLease, MachineResourcesStatus } from '@viko/protocol'
import { CircleAlert, Monitor } from 'lucide-react'
import type { ReactNode } from 'react'
import { Gauge } from './QuietMeter.js'
import { Ago } from './ui.js'

const GATE: Record<string, string> = {
  away: 'granted while you are away',
  approved: 'you tapped "Run now"',
  'awaiting-user': 'waits until you are away or tap "Run now"',
  declined: 'waits until you are away',
}
const KIND: Record<string, string> = {
  run: 'Agent run',
  landing: 'Landing gate',
  lane: 'Release lane',
  cli: 'Terminal',
}
const clock = (t: number) =>
  new Date(t).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

export function leaseSummary(l: MachineResourceLease): string {
  return `${l.label} — ${l.resources.join(' + ')}`
}

export function MachineResources({
  status,
  name,
  diskTrailing,
}: {
  status: MachineResourcesStatus
  name: string
  /** Appended to the disk line (Settings › Machines puts the doctor summary there). */
  diskTrailing?: ReactNode
}) {
  const holders = status.leases.filter((l) => l.state === 'granted')
  const waiting = status.leases.filter((l) => l.state === 'waiting')
  const shown = status.resources.filter(
    (r) => r.inUse > 0 || r.waiting > 0 || !r.name.includes(':'),
  )
  return (
    <div className="grid gap-3" data-testid="machine-resources">
      {status.disk?.belowFloor ? <DiskFloor disk={status.disk} /> : null}
      {shown.map((r) => (
        <Gauge
          key={r.name}
          label={r.name}
          value={
            <>
              {r.inUse} of {r.capacity}
              {r.waiting ? ` · ${r.waiting} waiting` : ''}
            </>
          }
          pct={r.capacity ? (100 * r.inUse) / r.capacity : 100}
          meterLabel={`${name} ${r.name} in use`}
          {...(r.waiting ? { wait: true } : {})}
        />
      ))}
      <p className="m-0 flex items-start gap-2 text-[14px] leading-5 text-text-2">
        <Monitor size={16} aria-hidden="true" className="mt-0.5 flex-none" />
        <span>
          Screen:{' '}
          {status.userAway
            ? 'you are away, UI tests may run'
            : 'you are here, UI tests wait (or tap "Run now" in the Inbox)'}
        </span>
      </p>
      <LeaseList title="Holding" empty="Nothing is holding a resource." leases={holders} />
      <LeaseList title="Waiting" empty="Nobody is waiting." leases={waiting} numbered />
      {status.disk && !status.disk.belowFloor ? (
        <p className="m-0 text-[14px] leading-5 text-text-2 tabular-nums">
          Disk: <span>{status.disk.freeGb} GB</span> free{' '}
          <span className="text-text-3">(floor {status.disk.floorGb} GB)</span>
          {diskTrailing}
        </p>
      ) : diskTrailing ? (
        <p className="m-0 text-[14px] leading-5 text-text-2">{diskTrailing}</p>
      ) : null}
      {status.recent?.length ? (
        <details className="st-lease text-[14px]">
          <summary>Recently finished ({status.recent.length})</summary>
          <ul className="m-0 mt-1 grid list-none gap-0 p-0">
            {status.recent.map((l) => (
              <LeaseRow key={l.ticketId} lease={l} />
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  )
}

function LeaseList({
  title,
  empty,
  leases,
  numbered,
}: {
  title: string
  empty: string
  leases: MachineResourceLease[]
  numbered?: boolean
}) {
  return (
    <div className="grid gap-0.5 text-[14px]">
      <span className="text-text-2">
        {title} ({leases.length})
      </span>
      {leases.length === 0 ? (
        <span className="text-text-3">{empty}</span>
      ) : (
        <ul className="m-0 grid list-none gap-0 p-0">
          {leases.map((l, i) => (
            <LeaseRow key={l.ticketId} lease={l} {...(numbered ? { position: i + 1 } : {})} />
          ))}
        </ul>
      )}
    </div>
  )
}

function LeaseRow({ lease: l, position }: { lease: MachineResourceLease; position?: number }) {
  return (
    <li>
      <details className="st-lease" data-testid="lease-row">
        <summary>
          {position ? <span className="text-text-3">#{position} </span> : null}
          {leaseSummary(l)}
          {l.state !== 'granted' && l.state !== 'waiting' ? (
            <span className="text-text-3"> · {l.state}</span>
          ) : null}
        </summary>
        <dl className="m-0 mt-1 mb-2 ml-5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-text-2">
          <dt>Kind</dt>
          <dd className="m-0">{KIND[l.kind] ?? l.kind}</dd>
          {l.command ? (
            <>
              <dt>Command</dt>
              <dd className="m-0 break-all font-mono text-[14px]">{l.command}</dd>
            </>
          ) : null}
          <dt>Resources</dt>
          <dd className="m-0">{l.resources.join(', ')}</dd>
          <dt>Priority</dt>
          <dd className="m-0">{l.priority}</dd>
          <dt>Queued</dt>
          <dd className="m-0 whitespace-nowrap">
            {clock(l.enqueuedAt)} (<Ago ts={l.enqueuedAt} />)
          </dd>
          {l.grantedAt ? (
            <>
              <dt>Granted</dt>
              <dd className="m-0 whitespace-nowrap">
                {clock(l.grantedAt)} (<Ago ts={l.grantedAt} />)
              </dd>
            </>
          ) : null}
          {l.expiresAt ? (
            <>
              <dt>Lease until</dt>
              <dd className="m-0 whitespace-nowrap">{clock(l.expiresAt)} unless renewed</dd>
            </>
          ) : null}
          {l.estimateMin ? (
            <>
              <dt>Estimate</dt>
              <dd className="m-0">~{l.estimateMin} min</dd>
            </>
          ) : null}
          {l.screenGate ? (
            <>
              <dt>Screen</dt>
              <dd className="m-0">{GATE[l.screenGate] ?? l.screenGate}</dd>
            </>
          ) : null}
          {l.reason ? (
            <>
              <dt>Why</dt>
              <dd className="m-0">{l.reason}</dd>
            </>
          ) : null}
          {l.runId ? (
            <>
              <dt>Run</dt>
              <dd className="m-0">
                <Link to="/s/$runId" params={{ runId: l.runId }} className="underline">
                  {l.runId}
                </Link>
              </dd>
            </>
          ) : null}
        </dl>
      </details>
    </li>
  )
}

function DiskFloor({ disk }: { disk: NonNullable<MachineResourcesStatus['disk']> }) {
  return (
    <div role="status" className="grid gap-2 text-[14px]">
      <div className="flex items-start gap-2 text-status-wait">
        <CircleAlert size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
        <span>
          <strong>
            Disk below the floor: {disk.freeGb} GB free (floor {disk.floorGb} GB).
          </strong>{' '}
          New worktrees and heavy jobs are paused until space is freed; running work continues. Viko
          never deletes anything for you.
        </span>
      </div>
      {disk.suggestions?.length ? (
        <ul className="m-0 grid list-none gap-1.5 p-0 text-text-2">
          {disk.suggestions.map((x) => (
            <li key={`${x.title}${x.path ?? ''}`} className="grid gap-0.5">
              <span>
                {x.title}
                {x.sizeGb !== undefined ? (
                  <span className="text-text-3"> · {x.sizeGb} GB</span>
                ) : null}
              </span>
              {x.command ? <code className="break-all text-[14px]">{x.command}</code> : null}
              {x.note ? <span className="text-text-3">{x.note}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
